import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { getLandingPageDistribution, getLandingPageFunnel } from "../../../../util/analyticsApi";

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelectContainer = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SelectLabel = styled.label`
  font-weight: bold;
  font-size: 14px;
`;

const PathSelect = styled.select`
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 250px;

  @media (max-width: 768px) {
    min-width: unset;
    width: 100%;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
`;

const SubsectionTitle = styled.h4`
  margin: 20px 0 10px;
  color: #555;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DepthTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
`;

const DepthRow = styled.tr`
  &:nth-child(even) {
    background: #f9f9f9;
  }
`;

const DepthLabel = styled.td`
  padding: 8px 12px;
  font-size: 13px;
  color: #555;
`;

const DepthValue = styled.td`
  padding: 8px 12px;
  font-size: 13px;
  font-weight: bold;
  text-align: right;
  color: #333;
`;

const CONVERSION_STEPS = [
  { key: "searched", label: "Searched", color: "#2196f3" },
  { key: "clickedRestaurant", label: "Clicked Restaurant", color: "#ff9800" },
  { key: "viewedDetails", label: "Viewed Details", color: "#9c27b0" },
  { key: "scrolledOptions", label: "Scrolled Options", color: "#00bcd4" },
  { key: "clickedWebsite", label: "Clicked Website", color: "#f44336" },
  { key: "clickedDirections", label: "Clicked Directions", color: "#795548" },
];

const DEPTH_METRICS = [
  { key: "searches", label: "Searches per session" },
  { key: "restaurantClicks", label: "Restaurants clicked per session" },
  { key: "detailViews", label: "Detail views per session" },
  { key: "optionScrolls", label: "Options scrolled per session" },
  { key: "websiteClicks", label: "Website clicks per session" },
  { key: "directionsClicks", label: "Direction clicks per session" },
];

const LandingPageFunnel = ({ filters }) => {
  const [paths, setPaths] = useState([]);
  const [selectedPath, setSelectedPath] = useState("");
  const [funnelData, setFunnelData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pathsLoading, setPathsLoading] = useState(true);

  useEffect(function fetchLandingPathsWhenFiltersChange() {
    const loadPaths = async () => {
      setPathsLoading(true);
      try {
        const result = await getLandingPageDistribution(filters);
        const seen = new Set();
        const allPaths = [];
        result.forEach((item) => {
          if (!seen.has(item.path)) {
            seen.add(item.path);
            allPaths.push({ path: item.path, count: item.count });
          }
          item.children.forEach((child) => {
            if (!seen.has(child.path)) {
              seen.add(child.path);
              allPaths.push({ path: child.path, count: child.count });
            }
          });
        });
        setPaths(allPaths);
      } catch (err) {
        console.error("Error loading landing paths:", err);
        setPaths([]);
      } finally {
        setPathsLoading(false);
      }
    };
    loadPaths();
  }, [filters]);

  useEffect(function fetchFunnelDataWhenPathChanges() {
    if (!selectedPath) {
      setFunnelData(null);
      return;
    }
    const loadFunnel = async () => {
      setLoading(true);
      try {
        const result = await getLandingPageFunnel({ ...filters, landingPath: selectedPath });
        setFunnelData(result);
      } catch (err) {
        console.error("Error loading landing funnel:", err);
        setFunnelData(null);
      } finally {
        setLoading(false);
      }
    };
    loadFunnel();
  }, [selectedPath, filters]);

  useEffect(function resetSelectedPathWhenFiltersChange() {
    setSelectedPath("");
  }, [filters]);

  // Build chart data from conversion stats
  const chartData = funnelData
    ? [
        { name: "Landed", value: funnelData.landed, color: "#4caf50", rate: "100%" },
        ...CONVERSION_STEPS.map((step) => {
          const count = funnelData.conversion[step.key] || 0;
          const rate = funnelData.landed > 0
            ? `${((count / funnelData.landed) * 100).toFixed(1)}%`
            : "0%";
          return { name: step.label, value: count, color: step.color, rate };
        }).filter((d) => d.value > 0),
      ]
    : [];

  // Filter depth metrics to only those with data
  const depthData = funnelData
    ? DEPTH_METRICS.filter((m) => (funnelData.medianDepth[m.key] || 0) > 0)
    : [];

  return (
    <Container>
      <SelectContainer>
        <SelectLabel>Landing Page:</SelectLabel>
        <PathSelect
          value={selectedPath}
          onChange={(e) => setSelectedPath(e.target.value)}
          disabled={pathsLoading}
        >
          <option value="">
            {pathsLoading ? "Loading paths..." : "Select a landing page"}
          </option>
          {paths.map((p) => (
            <option key={p.path} value={p.path}>
              {p.path} ({p.count.toLocaleString()} sessions)
            </option>
          ))}
        </PathSelect>
      </SelectContainer>

      {!selectedPath && (
        <NoDataMessage>Select a landing page to view its conversion funnel.</NoDataMessage>
      )}

      {loading && <LoadingMessage>Loading funnel data...</LoadingMessage>}

      {funnelData && !loading && (
        <>
          <SubsectionTitle>
            Session Conversion — % of sessions that reached each step
          </SubsectionTitle>
          <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 45)}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 80 }}>
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={140} />
              <Tooltip
                formatter={(value, _name, props) => [
                  `${value.toLocaleString()} sessions (${props.payload.rate})`,
                  "Sessions",
                ]}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
                <LabelList
                  dataKey="rate"
                  position="right"
                  style={{ fontSize: 12, fill: "#666" }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {depthData.length > 0 && (
            <>
              <SubsectionTitle>
                Engagement Depth — median per session (among sessions that engaged)
              </SubsectionTitle>
              <DepthTable>
                <tbody>
                  {depthData.map((m) => (
                    <DepthRow key={m.key}>
                      <DepthLabel>{m.label}</DepthLabel>
                      <DepthValue>{funnelData.medianDepth[m.key]}</DepthValue>
                    </DepthRow>
                  ))}
                </tbody>
              </DepthTable>
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default LandingPageFunnel;
